'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Link } from '@ci/i18n/navigation';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@ci/ui/sidebar';
import { Trans } from '@ci/ui/trans';

export interface DocsSearchItem {
  label: string;
  url: string;
}

/**
 * Search bar rendered at the top of the docs left navigation (PAY-3535).
 *
 * Matches article titles case-insensitively (partial substring). While the
 * query is empty the full navigation tree (`children`) is shown untouched;
 * once the user types, the tree is replaced by a flat list of matching
 * articles, each linking to its own page. An empty result set shows a short
 * "no results" message rather than a blank panel.
 */
export function DocsNavigationSearch({
  items,
  children,
}: React.PropsWithChildren<{ items: DocsSearchItem[] }>) {
  const t = useTranslations('marketing');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Cmd+K (macOS) / Ctrl+K (Windows/Linux) focuses the docs search, matching
  // the app-wide command-palette shortcut (Brendan review, PAY-3535). This is
  // an external DOM subscription, so it belongs in an effect.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const trimmed = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!trimmed) {
      return null;
    }

    return items.filter((item) => item.label.toLowerCase().includes(trimmed));
  }, [items, trimmed]);

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
            aria-hidden
          />

          <SidebarInput
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('searchDocumentationPlaceholder')}
            aria-label={t('searchDocumentation')}
            // title-only, case-insensitive partial match (PAY-3535).
            // `text-foreground` is required: the input sits in the dark docs
            // sidebar (which sets a light `text-sidebar-foreground`) but keeps
            // the light `bg-background`, so without an explicit dark text
            // colour the typed text is invisible (Brendan review).
            className="text-foreground pl-8"
            data-test="docs-search-input"
          />
        </SidebarGroupContent>
      </SidebarGroup>

      {results === null ? (
        children
      ) : results.length === 0 ? (
        // Type-ahead results swap the DOM as the reader types, so the empty
        // state is announced to assistive tech (the tree, static navigation,
        // stays outside the live region above).
        <SidebarGroup role="status" aria-live="polite">
          <SidebarGroupContent>
            <p
              className="text-muted-foreground px-2 py-1.5 text-sm"
              data-test="docs-search-empty"
            >
              <Trans i18nKey="marketing.searchDocumentationNoResults" />
            </p>
          </SidebarGroupContent>
        </SidebarGroup>
      ) : (
        <SidebarGroup role="status" aria-live="polite">
          <SidebarGroupContent>
            <SidebarMenu data-test="docs-search-results">
              {results.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    render={<Link href={item.url} />}
                    className="text-sidebar-foreground/80 transition-all"
                  >
                    <span className="block max-w-full truncate">
                      {item.label}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
}
